import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  OnInit,
  output,
  signal
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, Observable, startWith } from 'rxjs';

import { BuyStickersFormComponent } from '~console/app/stickers/components/buy-stickers-form/buy-stickers-form.component';
import { User } from '~core/models/auth.models';
import { OrganizationUserInfo } from '~core/models/organization.models';
import { Permission } from '~core/models/roles.models';
import { ImportTaskService } from '~core/services/import-task.service';
import { BaseComponent } from '../../abstract-components/base.component';
import { ROUTES } from '../../constants/routes.constants';
import { InvoiceFormComponent } from '../modals/invoice-form/invoice-form.component';
import { InvoiceFacade } from '~console/app/~store/facades/invoice.facade';

import { Sticker } from '~core/models/sticker.models';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'toolbar',
  standalone: false,
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ToolbarComponent extends BaseComponent implements OnInit {
  private _breakpointObserver = inject(BreakpointObserver);
  private _importTaskService = inject(ImportTaskService);
  private _appRef = inject(ApplicationRef);
  private _document = inject(DOCUMENT);
  private _invoiceFacade = inject(InvoiceFacade);
  toggleMenu = output<void>();

  readonly establishmentsRoute = ROUTES.establishments;
  readonly permission = Permission;

  stickerAccountInfo$: Observable<Sticker | undefined> | undefined;

  private _currentUserFromStore = toSignal(this._authFacade.currentUser$, {
    initialValue: undefined
  });

  onToggleMenu(): void {
    this.toggleMenu.emit();
  }

  displayedCurrentUser = computed<OrganizationUserInfo | User | undefined>(
    () => this.currentUser() ?? this._currentUserFromStore()
  );

  currentOrganization = toSignal(this._dashboardFacade.currentOrganization$);

  avatarUrl = input<string>('/assets/icons/admin.svg');
  roleVisible = input<boolean>(true);
  currentUser = toSignal(this._authFacade.currentUser$, { initialValue: null });

  stickerStatistics$ = this._authFacade.stickerStatistics$;

  isLoggingOut$ = this._authFacade.isLoggingOut$;
  isHandset$: Observable<boolean> = this._breakpointObserver
    .observe(Breakpoints.Handset)
    .pipe(map(result => result.matches));

  isLayoutHidden$ = this._globalFacade.routeData$.pipe(
    map(routeData =>
      routeData ? routeData.data?.layoutHidden === true : false
    ),
    startWith(true)
  );

  tasksCount$ = this._importTaskService
    .list({ status: 'in(pending,running)' })
    .pipe(map(result => result.fullyItems));

  isSidenavCollapsed = false;
  routes = ROUTES;
  showStickerAlert = signal(true);

  ngOnInit(): void {
    this._authFacade
      .loadStickerStatistics()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  logout(): void {
    this._authFacade.logout();
  }

  openInvoiceFormModal(): void {
    this._invoiceFacade.resetState();

    this._dialogService.openSheet(InvoiceFormComponent, {
      panelClass: [
        'mat-dialog-custom',
        'drawer',
        'slide-in-right',
        'w-[800px]'
      ],
      maxWidth: '800px'
    });
  }

  toggleSidenav(): void {
    this.isSidenavCollapsed = !this.isSidenavCollapsed;
    setTimeout(() => {
      this._appRef.tick();
    }, 100);
  }

  openBuyStickersForm(): void {
    this._dialogService.openSheet(BuyStickersFormComponent, {
      maxWidth: '650px'
    });
  }

  dismissStickerAlert(event?: Event): void {
    event?.stopPropagation();
    this.showStickerAlert.set(false);
  }

  navigateToBackoffice(): void {
    const currentLocation = this._document.defaultView?.location;
    if (currentLocation) {
      currentLocation.href =
        this._constantsService.environment.backofficeBaseUrl;
    }
  }
}
